import {
  Accordion,
  Alert,
  Anchor,
  Badge,
  Card,
  Code,
  Divider,
  Group,
  Image,
  List,
  SimpleGrid,
  Stack,
  Table,
  Text,
  ThemeIcon,
  Title,
  rem,
} from "@mantine/core";
import { createStyles } from "@mantine/emotion";
import {
  IconAlertCircle,
  IconArrowRight,
  IconBrackets,
  IconCalendarEvent,
  IconCode,
  IconDatabase,
  IconInfoCircle,
  IconListDetails,
  IconQrcode,
  IconReceipt,
  IconSettings,
  IconTicket,
  IconUsers,
} from "@tabler/icons-react";
import Diagram from "../../../../assets/EventsStatusFlow.svg";
import { PageHeader } from "../../components/PageHeader";

const useStyles = createStyles((theme) => ({
  section: {
    marginBottom: theme.spacing.xl,
  },
  sectionTitle: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing.sm,
    marginBottom: theme.spacing.md,
  },
  card: {
    backgroundColor: theme.colors.dark[7],
    border: `1px solid ${theme.colors.dark[5]}`,
  },
  codeBlock: {
    backgroundColor: theme.colors.dark[8],
    border: `1px solid ${theme.colors.dark[5]}`,
    borderRadius: theme.radius.md,
    padding: theme.spacing.md,
    fontFamily: "monospace",
    fontSize: theme.fontSizes.sm,
    lineHeight: 1.6,
    overflowX: "auto" as const,
    whiteSpace: "pre" as const,
    color: theme.colors.dark[0],
  },
  mutationBadge: {
    fontFamily: "monospace",
    textTransform: "none" as const,
    fontWeight: 600,
  },
  statusTable: {
    "& td, & th": {
      padding: `${rem(8)} ${rem(12)}`,
    },
  },
}));

const GqlBlock = ({ children }: { children: string }) => {
  const { classes } = useStyles();
  return <div className={classes.codeBlock}>{children.trim()}</div>;
};

const SectionHeader = ({
  icon,
  title,
  id,
}: {
  icon: React.ReactNode;
  title: string;
  id: string;
}) => {
  const { classes } = useStyles();
  return (
    <div className={classes.sectionTitle} id={id}>
      <ThemeIcon size="md" radius="md" variant="light" color="cyan">
        {icon}
      </ThemeIcon>
      <Title order={2}>{title}</Title>
    </div>
  );
};

export const EventDocumentation = () => {
  const { classes } = useStyles();

  return (
    <div className="wp-fade-in">
      <PageHeader
        title="Events Documentation"
        subtitle="Complete reference for the events & venues management system"
        showBreadcrumbs={false}
      />

      {/* Table of Contents */}
      <Card className={classes.card} p="md" radius="md" mb="xl">
        <Text fw={600} mb="sm">
          Table of Contents
        </Text>
        <SimpleGrid cols={{ base: 1, md: 3 }}>
          <List spacing={4} size="sm">
            <List.Item>
              <Anchor href="#overview">Overview</Anchor>
            </List.Item>
            <List.Item>
              <Anchor href="#lifecycle">Event Lifecycle</Anchor>
            </List.Item>
            <List.Item>
              <Anchor href="#reservations">Reservations</Anchor>
            </List.Item>
          </List>
          <List spacing={4} size="sm">
            <List.Item>
              <Anchor href="#reservation-status">
                Reservation Status Flow
              </Anchor>
            </List.Item>
            <List.Item>
              <Anchor href="#groups">Groups</Anchor>
            </List.Item>
            <List.Item>
              <Anchor href="#tickets">Ticket Configurations</Anchor>
            </List.Item>
          </List>
          <List spacing={4} size="sm">
            <List.Item>
              <Anchor href="#venues">Venues & Seats</Anchor>
            </List.Item>
            <List.Item>
              <Anchor href="#payment">Payment & QR Codes</Anchor>
            </List.Item>
            <List.Item>
              <Anchor href="#graphql">GraphQL API Reference</Anchor>
            </List.Item>
            <List.Item>
              <Anchor href="#statuses">Status Reference</Anchor>
            </List.Item>
          </List>
        </SimpleGrid>
      </Card>

      <Stack gap="xl">
        {/* ===================== OVERVIEW ===================== */}
        <div className={classes.section}>
          <SectionHeader
            icon={<IconCalendarEvent size={18} />}
            title="Overview"
            id="overview"
          />
          <Text mb="md">
            The Events feature allows anyone to create and manage events with a
            complete ticketing system, venue management and reservation
            workflow. It has been designed to be as flexible as possible,
            allowing you to create events for any kind of occasion, from esports
            tournaments to concerts, conferences or meetups.
          </Text>
          <Text mb="md">
            Events are composed of a hierarchy of nested entities:
          </Text>
          <Card className={classes.card} p="md" radius="md">
            <GqlBlock>
              {`Event
  +-- Configuration (registration dates, group sizes, custom fields, seat mode)
  +-- Reservations[] (user registrations with payment & ticket info)
  |    +-- Tickets[] (individual tickets purchased within a reservation)
  |    +-- Group (optional group of participants)
  +-- Ticket Configurations[] (ticket types: VIP, Standard, Early Bird, etc.)
  +-- Venue (optional physical location)
       +-- Seat Types[] (VIP, Standard, Balcony, etc.)
            +-- Seats[] (individual seats with positions)`}
            </GqlBlock>
          </Card>

          <Alert
            icon={<IconInfoCircle />}
            color="indigo"
            variant="light"
            mt="md"
          >
            By combining the Events feature with the Platform feature, you can
            create your own customized website to sell tickets, manage your
            events and more!
          </Alert>
        </div>

        {/* ===================== LIFECYCLE ===================== */}
        <div className={classes.section}>
          <SectionHeader
            icon={<IconListDetails size={18} />}
            title="Event Lifecycle"
            id="lifecycle"
          />
          <Text mb="md">
            An event progresses through a series of date-driven states. The
            administrator configures key dates that control when the event
            becomes visible, when registrations open/close, and when the event
            starts/ends.
          </Text>

          <Card className={classes.card} p="md" radius="md" mb="md">
            <GqlBlock>
              {`DRAFT -----> VISIBLE -----> REGISTRATION -----> ACTIVE -----> ENDED
  |               |                |                |             |
  No visibleAt    visibleAt        startRegistra-   startAt       endAt
  or not yet      reached          tionsAt reached  reached       reached
  visible`}
            </GqlBlock>
          </Card>

          <Title order={4} mb="xs">
            Lifecycle Details
          </Title>
          <List spacing="xs" mb="md">
            <List.Item>
              <Text>
                <Badge color="gray" variant="light" size="sm" mr="xs">
                  draft
                </Badge>
                No <Code>visibleAt</Code> set, or current time is before{" "}
                <Code>visibleAt</Code>
              </Text>
            </List.Item>
            <List.Item>
              <Text>
                <Badge color="blue" variant="light" size="sm" mr="xs">
                  visible
                </Badge>
                Event is visible but registrations haven't opened yet (before{" "}
                <Code>startRegistrationsAt</Code>)
              </Text>
            </List.Item>
            <List.Item>
              <Text>
                <Badge color="cyan" variant="light" size="sm" mr="xs">
                  registration
                </Badge>
                Between <Code>startRegistrationsAt</Code> and{" "}
                <Code>endRegistrationsAt</Code> — users can create reservations
              </Text>
            </List.Item>
            <List.Item>
              <Text>
                <Badge color="green" variant="light" size="sm" mr="xs">
                  active
                </Badge>
                Current time is after <Code>startAt</Code> — the event is
                happening
              </Text>
            </List.Item>
            <List.Item>
              <Text>
                <Badge color="dark" variant="light" size="sm" mr="xs">
                  ended
                </Badge>
                Current time is after <Code>endAt</Code>
              </Text>
            </List.Item>
          </List>
        </div>

        {/* ===================== RESERVATIONS ===================== */}
        <div className={classes.section}>
          <SectionHeader
            icon={<IconReceipt size={18} />}
            title="Reservations"
            id="reservations"
          />
          <Text mb="md">
            A reservation represents a user's registration to an event. It
            contains all information about the user, their group, their tickets
            and their payment status. When a user registers to an event, a
            reservation is automatically created.
          </Text>

          <Title order={4} mb="xs">
            Registration Flow
          </Title>
          <Text mb="sm">
            Depending on the event configuration, the user may need to:
          </Text>
          <List spacing="xs" mb="md">
            <List.Item>Wait for admin approval before proceeding</List.Item>
            <List.Item>
              Start a payment session to select and purchase tickets
            </List.Item>
            <List.Item>Simply register without any payment required</List.Item>
          </List>

          <Accordion variant="separated" mb="md">
            <Accordion.Item value="session">
              <Accordion.Control>
                <Group gap="xs">
                  <IconArrowRight size={16} />
                  <Text fw={500}>Session Duration</Text>
                </Group>
              </Accordion.Control>
              <Accordion.Panel>
                <Text size="sm">
                  The session duration (how long a user has to complete their
                  payment) is configurable per event. The default is 900 seconds
                  (15 minutes). When the session expires, the reservation
                  transitions to <Code>SESSION_EXPIRED</Code>.
                </Text>
              </Accordion.Panel>
            </Accordion.Item>

            <Accordion.Item value="options">
              <Accordion.Control>
                <Group gap="xs">
                  <IconSettings size={16} />
                  <Text fw={500}>Registration Options</Text>
                </Group>
              </Accordion.Control>
              <Accordion.Panel>
                <List size="sm" spacing="xs">
                  <List.Item>
                    <strong>Allow new session after timeout</strong> — Whether
                    users can start a new session after their previous session
                    has expired
                  </List.Item>
                  <List.Item>
                    <strong>Allow more tickets than group members</strong> —
                    Whether to allow reserving more tickets than the number of
                    group members. When disabled, a reservation must hold
                    exactly as many tickets as its group has members; when
                    enabled, a reservation may hold up to{" "}
                    <Code>maxTicketsPerReservation</Code> tickets regardless of
                    member count. The rule is enforced when starting a
                    registration session, when updating tickets, and again when
                    validating and paying — exceeding it returns a{" "}
                    <Code>BAD_REQUEST</Code> error.
                  </List.Item>
                  <List.Item>
                    <strong>Max tickets per reservation</strong> — The upper
                    bound on tickets a single reservation can hold. It must
                    always be at least the maximum group size; sending a smaller
                    value returns a <Code>BAD_CONFIGURATION</Code> error. When{" "}
                    <strong>Allow more tickets than group members</strong> is
                    off, this value is irrelevant (tickets are capped by member
                    count), so the console disables the field and sets it to the
                    maximum group size automatically.
                  </List.Item>
                  <List.Item>
                    <strong>Status after registration</strong> — e.g. directly
                    start a payment session, require admin approval first, or
                    just register
                  </List.Item>
                  <List.Item>
                    <strong>Status after payment</strong> — e.g. confirmed,
                    awaiting presence confirmation, or registered and awaiting
                    admin approval
                  </List.Item>
                </List>
              </Accordion.Panel>
            </Accordion.Item>

            <Accordion.Item value="csv">
              <Accordion.Control>
                <Group gap="xs">
                  <IconDatabase size={16} />
                  <Text fw={500}>CSV Export</Text>
                </Group>
              </Accordion.Control>
              <Accordion.Panel>
                <Text size="sm">
                  The CSV export in the Reservations page includes all group
                  members with their player profiles, identities and custom
                  field values.
                </Text>
              </Accordion.Panel>
            </Accordion.Item>
          </Accordion>
        </div>

        {/* ===================== RESERVATION STATUS ===================== */}
        <div className={classes.section}>
          <SectionHeader
            icon={<IconArrowRight size={18} />}
            title="Reservation Status Flow"
            id="reservation-status"
          />
          <Text mb="md">
            The reservation status flow defines how a reservation transitions
            between states depending on user actions and event configuration.
            The flow can be customised per event through the Registration
            Settings page.
          </Text>

          <Card withBorder p="lg" radius="md" mb="md">
            <Image src={Diagram} alt="Event reservation status flow" />
          </Card>

          <Title order={4} mb="xs">
            Reservation Statuses
          </Title>
          <List spacing="xs" mb="md">
            <List.Item>
              <Badge color="gray" variant="light" size="sm" mr="xs">
                NONE
              </Badge>
              Reservation created, user has registered to the event
            </List.Item>
            <List.Item>
              <Badge color="yellow" variant="light" size="sm" mr="xs">
                REQUIRES_ADMIN_APPROVAL
              </Badge>
              Waiting for an admin to approve before the user can proceed
            </List.Item>
            <List.Item>
              <Badge color="blue" variant="light" size="sm" mr="xs">
                PENDING
              </Badge>
              User is in an active payment session and can still update their
              reservation
            </List.Item>
            <List.Item>
              <Badge color="orange" variant="light" size="sm" mr="xs">
                PAYMENT_PENDING
              </Badge>
              Payment has been initiated and is being processed
            </List.Item>
            <List.Item>
              <Badge color="red" variant="light" size="sm" mr="xs">
                PAYMENT_FAILED
              </Badge>
              Payment was declined or failed
            </List.Item>
            <List.Item>
              <Badge color="red" variant="light" size="sm" mr="xs">
                PAYMENT_REFUNDED
              </Badge>
              Payment was refunded to the user
            </List.Item>
            <List.Item>
              <Badge color="dark" variant="light" size="sm" mr="xs">
                SESSION_EXPIRED
              </Badge>
              The reservation session timed out before the user completed
              payment
            </List.Item>
            <List.Item>
              <Badge color="cyan" variant="light" size="sm" mr="xs">
                REGISTERED
              </Badge>
              Payment validated, waiting for admin approval
            </List.Item>
            <List.Item>
              <Badge color="yellow" variant="light" size="sm" mr="xs">
                PRESENCE_CONFIRMATION_PENDING
              </Badge>
              Awaiting user to confirm their presence at the event
            </List.Item>
            <List.Item>
              <Badge color="green" variant="light" size="sm" mr="xs">
                CONFIRMED
              </Badge>
              Fully confirmed and participating
            </List.Item>
            <List.Item>
              <Badge color="gray" variant="light" size="sm" mr="xs">
                NOT_ATTENDING
              </Badge>
              User or admin marked as not attending
            </List.Item>
            <List.Item>
              <Badge color="red" variant="light" size="sm" mr="xs">
                DENIED
              </Badge>
              Admin denied the reservation
            </List.Item>
          </List>

          <Alert icon={<IconAlertCircle />} color="yellow" variant="light">
            When changing a reservation status to a state that would trigger a
            payment refund, the system will ask for confirmation before
            proceeding. This action is irreversible. Whenever a reservation
            moves to a status that no longer holds a seat (for example DENIED),
            its seat is automatically freed and put back on sale. This happens
            independently of the refund: declining the refund only leaves the
            payment untouched — the seat is always released as soon as the
            reservation stops holding it.
          </Alert>
        </div>

        {/* ===================== GROUPS ===================== */}
        <div className={classes.section}>
          <SectionHeader
            icon={<IconUsers size={18} />}
            title="Groups"
            id="groups"
          />
          <Text mb="md">
            Groups allow users to invite others to their reservation and assign
            them tickets. This is useful for team events, group bookings or any
            event where multiple participants register together.
          </Text>

          <Title order={4} mb="xs">
            Group Status
          </Title>
          <SimpleGrid cols={{ base: 1, md: 2 }} mb="md">
            <Card className={classes.card} p="md" radius="md">
              <Group mb="xs">
                <Badge color="green">VALID</Badge>
              </Group>
              <Text size="sm">
                The group meets the size requirements and all members have
                accepted the invitation.
              </Text>
            </Card>
            <Card className={classes.card} p="md" radius="md">
              <Group mb="xs">
                <Badge color="red">INVALID</Badge>
              </Group>
              <Text size="sm">
                The group does not meet the requirements (too few or too many
                members, or members have not accepted yet).
              </Text>
            </Card>
          </SimpleGrid>

          <Text size="sm" mb="sm">
            Groups are configured with a minimum and maximum size. Group members
            are tracked through the User Groups system. Each member has a player
            profile that can be queried for their identities (linked accounts)
            and custom field values.
          </Text>
        </div>

        {/* ===================== TICKETS ===================== */}
        <div className={classes.section}>
          <SectionHeader
            icon={<IconTicket size={18} />}
            title="Ticket Configurations"
            id="tickets"
          />
          <Text mb="md">
            Ticket configurations define the types of tickets available for an
            event. Multiple ticket configurations can coexist on a single event
            (e.g. VIP, Standard, Early Bird). Each has its own inventory tracked
            by current count vs max count.
          </Text>

          <Title order={4} mb="xs">
            Ticket Configuration Fields
          </Title>
          <Table
            striped
            highlightOnHover
            className={classes.statusTable}
            mb="md"
          >
            <thead>
              <tr>
                <th>Field</th>
                <th>Type</th>
                <th>Description</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td>
                  <Code>name</Code>
                </td>
                <td>String!</td>
                <td>Displayed to users during ticket selection</td>
              </tr>
              <tr>
                <td>
                  <Code>description</Code>
                </td>
                <td>String</td>
                <td>Additional details about the ticket type</td>
              </tr>
              <tr>
                <td>
                  <Code>price</Code>
                </td>
                <td>Float!</td>
                <td>Ticket price (stored in cents internally)</td>
              </tr>
              <tr>
                <td>
                  <Code>currency</Code>
                </td>
                <td>String!</td>
                <td>Currency code (e.g. EUR, USD)</td>
              </tr>
              <tr>
                <td>
                  <Code>maxCount</Code>
                </td>
                <td>Float!</td>
                <td>Maximum tickets available for this type</td>
              </tr>
              <tr>
                <td>
                  <Code>seatTypeId</Code>
                </td>
                <td>ID</td>
                <td>
                  Optionally link to a venue seat type — auto-sets maxCount to
                  available seats
                </td>
              </tr>
              <tr>
                <td>
                  <Code>buyable</Code>
                </td>
                <td>Boolean!</td>
                <td>Whether users can purchase this ticket</td>
              </tr>
              <tr>
                <td>
                  <Code>customFields</Code>
                </td>
                <td>[Property]</td>
                <td>Additional fields users must fill when purchasing</td>
              </tr>
            </tbody>
          </Table>

          <Alert icon={<IconInfoCircle />} color="indigo" variant="light">
            Changing the seat type of an existing ticket configuration will
            affect all existing reservations. Tickets will remain accessible but
            will no longer be assigned to a specific seat.
          </Alert>
        </div>

        {/* ===================== CUSTOM FIELDS ===================== */}
        <div className={classes.section}>
          <SectionHeader
            icon={<IconBrackets size={18} />}
            title="Custom Fields"
            id="custom-fields"
          />
          <Text mb="md">
            Custom fields allow you to collect additional information from users
            during the registration process. They can be configured at two
            levels:
          </Text>

          <SimpleGrid cols={{ base: 1, md: 2 }} mb="md">
            <Card className={classes.card} p="md" radius="md">
              <Text fw={600} mb="xs">
                Event Level
              </Text>
              <Text size="sm">
                Fields that apply to all reservations for the event. Configured
                in Settings &gt; Custom Fields.
              </Text>
            </Card>
            <Card className={classes.card} p="md" radius="md">
              <Text fw={600} mb="xs">
                Ticket Configuration Level
              </Text>
              <Text size="sm">
                Fields specific to a particular ticket type. Configured within
                each ticket configuration.
              </Text>
            </Card>
          </SimpleGrid>

          <Title order={4} mb="xs">
            Custom Field Properties
          </Title>
          <Table
            striped
            highlightOnHover
            className={classes.statusTable}
            mb="md"
          >
            <thead>
              <tr>
                <th>Property</th>
                <th>Description</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td>
                  <Code>name</Code>
                </td>
                <td>The field label displayed to users</td>
              </tr>
              <tr>
                <td>
                  <Code>type</Code>
                </td>
                <td>Data type (text, number, etc.)</td>
              </tr>
              <tr>
                <td>
                  <Code>required</Code>
                </td>
                <td>Whether the field must be filled</td>
              </tr>
              <tr>
                <td>
                  <Code>unique</Code>
                </td>
                <td>
                  Whether the value must be unique across all reservations
                </td>
              </tr>
              <tr>
                <td>
                  <Code>visibility</Code>
                </td>
                <td>
                  Who can see the field value (public, private, admin-only)
                </td>
              </tr>
              <tr>
                <td>
                  <Code>editability</Code>
                </td>
                <td>
                  When the field can be edited (during registration, after,
                  never)
                </td>
              </tr>
            </tbody>
          </Table>
        </div>

        {/* ===================== VENUES & SEATS ===================== */}
        <div className={classes.section}>
          <SectionHeader
            icon={<IconDatabase size={18} />}
            title="Venues & Seats"
            id="venues"
          />
          <Text mb="md">
            Venues represent physical locations where events take place. They
            provide a complete seating management system with seat types and
            individual seat placement.
          </Text>

          <Accordion variant="separated" mb="md">
            <Accordion.Item value="venues">
              <Accordion.Control>
                <Group gap="xs">
                  <IconDatabase size={16} />
                  <Text fw={500}>Venues</Text>
                </Group>
              </Accordion.Control>
              <Accordion.Panel>
                <Text size="sm" mb="xs">
                  A venue is defined by its name, description and address (city,
                  state, postal code, country). It can optionally have an image
                  (floor plan or map) uploaded through a two-step process: first
                  request a pre-signed upload URL, then validate the uploaded
                  image.
                </Text>
                <Alert
                  icon={<IconInfoCircle />}
                  color="indigo"
                  variant="light"
                  mt="xs"
                >
                  Venues are shared across your organization. A single venue can
                  be assigned to multiple events.
                </Alert>
              </Accordion.Panel>
            </Accordion.Item>

            <Accordion.Item value="seat-types">
              <Accordion.Control>
                <Group gap="xs">
                  <IconListDetails size={16} />
                  <Text fw={500}>Seat Types</Text>
                </Group>
              </Accordion.Control>
              <Accordion.Panel>
                <Text size="sm" mb="xs">
                  Seat types define categories of seats within a venue (e.g.
                  VIP, Standard, Balcony, Floor). They are used to:
                </Text>
                <List size="sm" spacing="xs">
                  <List.Item>
                    Categorize individual seats in the venue editor
                  </List.Item>
                  <List.Item>
                    Link ticket configurations to specific seat categories
                  </List.Item>
                  <List.Item>
                    Automatically set ticket max count based on available seats
                  </List.Item>
                </List>
              </Accordion.Panel>
            </Accordion.Item>

            <Accordion.Item value="seats">
              <Accordion.Control>
                <Group gap="xs">
                  <IconSettings size={16} />
                  <Text fw={500}>Seats & Assignment Modes</Text>
                </Group>
              </Accordion.Control>
              <Accordion.Panel>
                <Text size="sm" mb="xs">
                  Individual seats are created within a venue and assigned to a
                  seat type. Each seat has a position (x, y coordinates) and
                  size for rendering on the venue map. Seats are managed through
                  a bulk update API.
                </Text>
                <Text size="sm" fw={600} mt="sm" mb="xs">
                  Seat Assignment Modes:
                </Text>
                <List size="sm" spacing="xs">
                  <List.Item>
                    <Badge color="green" variant="light" size="sm" mr="xs">
                      Optional
                    </Badge>
                    Users can choose a seat but are not required to
                  </List.Item>
                  <List.Item>
                    <Badge color="blue" variant="light" size="sm" mr="xs">
                      Required
                    </Badge>
                    Users must select a seat during registration
                  </List.Item>
                  <List.Item>
                    <Badge color="gray" variant="light" size="sm" mr="xs">
                      Disabled
                    </Badge>
                    Seat selection is not available
                  </List.Item>
                </List>
              </Accordion.Panel>
            </Accordion.Item>
          </Accordion>
        </div>

        {/* ===================== PAYMENT & QR ===================== */}
        <div className={classes.section}>
          <SectionHeader
            icon={<IconQrcode size={18} />}
            title="Payment & QR Codes"
            id="payment"
          />

          <SimpleGrid cols={{ base: 1, md: 2 }} mb="md">
            <Card className={classes.card} p="md" radius="md">
              <Text fw={600} mb="xs">
                Payment Integration (Stripe)
              </Text>
              <Text size="sm" mb="xs">
                The Events system integrates with Stripe for payment processing.
                When a user validates their reservation, the system returns a
                Stripe <Code>clientSecret</Code> and <Code>paymentUrl</Code>.
              </Text>
              <List size="sm" spacing={4}>
                <List.Item>
                  Configurable return URLs (success, cancel)
                </List.Item>
                <List.Item>
                  Automatic status transitions based on payment result
                </List.Item>
                <List.Item>Admin-initiated refunds with confirmation</List.Item>
              </List>
            </Card>
            <Card className={classes.card} p="md" radius="md">
              <Text fw={600} mb="xs">
                QR Code Scanning
              </Text>
              <Text size="sm" mb="xs">
                Each event has a decryption key used to validate QR codes on
                tickets. The QR code scanner in the console decrypts the ticket
                data and verifies it belongs to the current event.
              </Text>
              <Text size="sm">
                The encrypted ticket data contains the ticket ID, event ID and
                username, ensuring that tickets cannot be forged or reused
                across events.
              </Text>
            </Card>
          </SimpleGrid>
        </div>

        {/* ===================== GRAPHQL API ===================== */}
        <div className={classes.section}>
          <SectionHeader
            icon={<IconCode size={18} />}
            title="GraphQL API Reference"
            id="graphql"
          />
          <Text mb="md">
            Complete list of all GraphQL operations available for the Events
            service, including events, reservations, tickets, venues, seat types
            and seats. Endpoints marked{" "}
            <Badge color="yellow" variant="light" size="xs">
              Not used in console
            </Badge>{" "}
            are user-facing endpoints available through the API for your
            platform or mobile app integration.
          </Text>

          <Accordion variant="separated">
            <Accordion.Item value="event-queries">
              <Accordion.Control>
                <Group gap="xs">
                  <Badge color="green" className={classes.mutationBadge}>
                    QUERY
                  </Badge>
                  <Text>Event Queries</Text>
                </Group>
              </Accordion.Control>
              <Accordion.Panel>
                <Stack gap="md">
                  <div>
                    <Text fw={600} mb="xs">
                      event (detail)
                    </Text>
                    <Text size="sm" c="dimmed" mb="xs">
                      Fetch a single event with the current user's reservation.
                    </Text>
                    <GqlBlock>
                      {`query ($id: ID!) {
  event(id: $id) {
    id, title, description, status, startAt, endAt,
    startRegistrationsAt, endRegistrationsAt, visibleAt,
    configuration { ... },
    userReservation { id, status }
  }
}`}
                    </GqlBlock>
                  </div>
                  <Divider />
                  <div>
                    <Text fw={600} mb="xs">
                      events (list)
                    </Text>
                    <Text size="sm" c="dimmed" mb="xs">
                      Fetch list of events with pagination and filtering (by
                      status, visibility, players).
                    </Text>
                    <GqlBlock>
                      {`query ($cursor: String, $count: Float, $query: EventsQuery) {
  events(
    page: { first: $count, after: $cursor }
    query: $query
  ) {
    nodes { id, title, startAt, endAt, status }
    totalCount
    pageInfo { hasNextPage, endCursor }
  }
}`}
                    </GqlBlock>
                  </div>
                </Stack>
              </Accordion.Panel>
            </Accordion.Item>

            <Accordion.Item value="reservation-queries">
              <Accordion.Control>
                <Group gap="xs">
                  <Badge color="green" className={classes.mutationBadge}>
                    QUERY
                  </Badge>
                  <Text>Reservation Queries</Text>
                </Group>
              </Accordion.Control>
              <Accordion.Panel>
                <Stack gap="md">
                  <div>
                    <Text fw={600} mb="xs">
                      eventReservation (detail)
                    </Text>
                    <GqlBlock>
                      {`query ($reservationId: ID!) {
  eventReservation(reservationId: $reservationId) {
    id, status, createdAt, updatedAt
  }
}`}
                    </GqlBlock>
                  </div>
                  <Divider />
                  <div>
                    <Text fw={600} mb="xs">
                      eventReservations (list with filters)
                    </Text>
                    <Text size="sm" c="dimmed" mb="xs">
                      Fetch paginated reservations with advanced filtering
                      (group status, player properties, identity provider,
                      registration status).
                    </Text>
                    <GqlBlock>
                      {`query ($eventId: ID!, $cursor: String, $count: Float,
       $query: EventReservationsQueryInput!) {
  eventReservations(
    eventId: $eventId
    page: { first: $count, after: $cursor }
    query: $query
  ) {
    nodes { id, status }
    totalCount
    pageInfo { hasNextPage, endCursor }
  }
}`}
                    </GqlBlock>
                  </div>
                  <Divider />
                  <div>
                    <Text fw={600} mb="xs">
                      eventReservationsMyReservations
                    </Text>
                    <Badge color="yellow" variant="light" size="xs" mb="xs">
                      Not used in console
                    </Badge>
                    <Text size="sm" c="dimmed" mb="xs">
                      Fetch the current user's reservations across events.
                    </Text>
                    <GqlBlock>
                      {`query ($eventIds: [ID!], $groupStatus: UserGroupStatus,
       $page: PageInfo!, $status: EventReservationStatus) {
  eventReservationsMyReservations(
    eventIds: $eventIds, groupStatus: $groupStatus,
    page: $page, status: $status
  ) {
    nodes { id, status }
    totalCount
    pageInfo { hasNextPage, endCursor }
  }
}`}
                    </GqlBlock>
                  </div>
                </Stack>
              </Accordion.Panel>
            </Accordion.Item>

            <Accordion.Item value="event-mutations">
              <Accordion.Control>
                <Group gap="xs">
                  <Badge color="red" className={classes.mutationBadge}>
                    MUTATION
                  </Badge>
                  <Text>Event Mutations</Text>
                </Group>
              </Accordion.Control>
              <Accordion.Panel>
                <Stack gap="md">
                  <div>
                    <Text fw={600} mb="xs">
                      eventCreate
                    </Text>
                    <GqlBlock>
                      {`mutation ($input: EventCreateInput!) {
  eventCreate(input: $input) { id }
}`}
                    </GqlBlock>
                  </div>
                  <Divider />
                  <div>
                    <Text fw={600} mb="xs">
                      eventUpdate
                    </Text>
                    <GqlBlock>
                      {`mutation ($id: ID!, $input: EventUpdateInput!) {
  eventUpdate(id: $id, input: $input) { id }
}`}
                    </GqlBlock>
                  </div>
                  <Divider />
                  <div>
                    <Text fw={600} mb="xs">
                      eventDelete
                    </Text>
                    <GqlBlock>
                      {`mutation ($id: ID!) {
  eventDelete(id: $id)
}`}
                    </GqlBlock>
                  </div>
                </Stack>
              </Accordion.Panel>
            </Accordion.Item>

            <Accordion.Item value="reservation-mutations">
              <Accordion.Control>
                <Group gap="xs">
                  <Badge color="red" className={classes.mutationBadge}>
                    MUTATION
                  </Badge>
                  <Text>Reservation Mutations</Text>
                </Group>
              </Accordion.Control>
              <Accordion.Panel>
                <Stack gap="md">
                  <div>
                    <Text fw={600} mb="xs">
                      eventReservationCreate
                    </Text>
                    <Badge color="yellow" variant="light" size="xs" mb="xs">
                      Not used in console
                    </Badge>
                    <GqlBlock>
                      {`mutation ($eventId: ID!) {
  eventReservationCreate(eventId: $eventId) { id, status }
}`}
                    </GqlBlock>
                  </div>
                  <Divider />
                  <div>
                    <Text fw={600} mb="xs">
                      eventReservationStartRegistrationSession
                    </Text>
                    <Badge color="yellow" variant="light" size="xs" mb="xs">
                      Not used in console
                    </Badge>
                    <GqlBlock>
                      {`mutation ($eventId: ID!, $input: EventReservationRegisterTicketsInput!) {
  eventReservationStartRegistrationSession(
    eventId: $eventId, input: $input
  ) { id, status }
}`}
                    </GqlBlock>
                  </div>
                  <Divider />
                  <div>
                    <Text fw={600} mb="xs">
                      eventReservationUpdateStatus
                    </Text>
                    <Text size="sm" c="dimmed" mb="xs">
                      Update reservation status. Moving a reservation out of a
                      paid or payment-pending status requires
                      confirmPaymentRefundOrCancel: true. By default the
                      associated payment is refunded (or canceled if still
                      pending); pass refundPayment: false to change the status
                      while leaving the payment untouched.
                    </Text>
                    <GqlBlock>
                      {`mutation ($reservationId: ID!, $status: EventReservationStatus!,
         $confirmPaymentRefundOrCancel: Boolean, $refundPayment: Boolean) {
  eventReservationUpdateStatus(
    reservationId: $reservationId
    status: $status
    confirmPaymentRefundOrCancel: $confirmPaymentRefundOrCancel
    refundPayment: $refundPayment
  ) { id, status }
}`}
                    </GqlBlock>
                  </div>
                  <Divider />
                  <div>
                    <Text fw={600} mb="xs">
                      eventReservationConfirmPresence
                    </Text>
                    <GqlBlock>
                      {`mutation ($eventId: ID!, $confirmed: Boolean!) {
  eventReservationConfirmPresence(
    eventId: $eventId, confirmed: $confirmed
  ) { id, status }
}`}
                    </GqlBlock>
                  </div>
                  <Divider />
                  <div>
                    <Text fw={600} mb="xs">
                      eventReservationCancel
                    </Text>
                    <GqlBlock>
                      {`mutation ($reservationId: ID!) {
  eventReservationCancel(reservationId: $reservationId) { id, status }
}`}
                    </GqlBlock>
                  </div>
                  <Divider />
                  <div>
                    <Text fw={600} mb="xs">
                      eventReservationUpdateTickets
                    </Text>
                    <Text size="sm" c="dimmed" mb="xs">
                      Add, release or update custom fields on tickets within a
                      reservation.
                    </Text>
                    <GqlBlock>
                      {`mutation ($reservationId: ID!,
         $input: EventReservationUpdateTicketsInput!) {
  eventReservationUpdateTickets(
    reservationId: $reservationId, input: $input
  ) { id, status }
}`}
                    </GqlBlock>
                  </div>
                  <Divider />
                  <div>
                    <Text fw={600} mb="xs">
                      eventReservationValidateAndPay
                    </Text>
                    <Text size="sm" c="dimmed" mb="xs">
                      Validate and process payment (returns Stripe clientSecret
                      and paymentUrl).
                    </Text>
                    <GqlBlock>
                      {`mutation ($eventId: ID!, $options: EventReservationValidateAndPayInput!,
         $ticketConfigurations: [EventReservationTicketConfigurationInput!]) {
  eventReservationValidateAndPay(
    eventId: $eventId, options: $options,
    ticketConfigurations: $ticketConfigurations
  ) { clientSecret, paymentUrl }
}`}
                    </GqlBlock>
                  </div>
                </Stack>
              </Accordion.Panel>
            </Accordion.Item>

            <Accordion.Item value="ticket-operations">
              <Accordion.Control>
                <Group gap="xs">
                  <Badge color="cyan" className={classes.mutationBadge}>
                    TICKETS
                  </Badge>
                  <Text>Ticket Configuration Operations</Text>
                </Group>
              </Accordion.Control>
              <Accordion.Panel>
                <Stack gap="md">
                  <div>
                    <Text fw={600} mb="xs">
                      eventTicketConfiguration / eventTicketConfigurations
                    </Text>
                    <GqlBlock>
                      {`query ($id: ID!) {
  eventTicketConfiguration(id: $id) {
    id, name, description, price, currency,
    maxCount, currentCount, buyable, seatTypeId,
    configuration { customFields { ... } }
  }
}

query ($eventId: ID!) {
  eventTicketConfigurations(eventId: $eventId) {
    id, name, price, currency, maxCount, currentCount, buyable
  }
}`}
                    </GqlBlock>
                  </div>
                  <Divider />
                  <div>
                    <Text fw={600} mb="xs">
                      Ticket Configuration Mutations
                    </Text>
                    <GqlBlock>
                      {`mutation ($input: EventTicketConfigurationCreateInput!) {
  eventTicketConfigurationCreate(input: $input) { id }
}

mutation ($ticketConfigurationId: ID!,
         $input: EventTicketConfigurationUpdateInput!) {
  eventTicketConfigurationUpdate(
    ticketConfigurationId: $ticketConfigurationId, input: $input
  ) { id }
}

mutation ($ticketConfigurationId: ID!) {
  eventTicketConfigurationDelete(
    ticketConfigurationId: $ticketConfigurationId
  ) { id }
}`}
                    </GqlBlock>
                  </div>
                </Stack>
              </Accordion.Panel>
            </Accordion.Item>

            <Accordion.Item value="venue-operations">
              <Accordion.Control>
                <Group gap="xs">
                  <Badge color="orange" className={classes.mutationBadge}>
                    VENUES
                  </Badge>
                  <Text>Venue, Seat Type & Seat Operations</Text>
                </Group>
              </Accordion.Control>
              <Accordion.Panel>
                <Stack gap="md">
                  <div>
                    <Text fw={600} mb="xs">
                      Venue Queries
                    </Text>
                    <GqlBlock>
                      {`query ($id: ID!) {
  eventVenue(id: $id) {
    id, name, description, address, city, state, postalCode, country, imageUrl
  }
}

query ($page: PageInfo!) {
  eventVenues(page: $page) {
    nodes { id, name, description, city, country }
    totalCount
    pageInfo { hasNextPage, endCursor }
  }
}`}
                    </GqlBlock>
                  </div>
                  <Divider />
                  <div>
                    <Text fw={600} mb="xs">
                      Venue Mutations
                    </Text>
                    <GqlBlock>
                      {`mutation ($input: EventVenueCreateInput!) {
  eventVenueCreate(input: $input) { id }
}

mutation ($id: ID!, $input: EventVenueUpdateInput!) {
  eventVenueUpdate(id: $id, input: $input) { id }
}

mutation ($id: ID!) {
  eventVenueDelete(id: $id) { id }
}

# Image upload flow
mutation ($id: ID!, $size: Float!) {
  eventVenueRequestImageUpdate(id: $id, size: $size) { url, key }
}

mutation ($id: ID!) {
  eventVenueValidateImage(id: $id) { id, imageUrl }
}`}
                    </GqlBlock>
                  </div>
                  <Divider />
                  <div>
                    <Text fw={600} mb="xs">
                      Seat Type Operations
                    </Text>
                    <GqlBlock>
                      {`query ($venueId: ID!, $page: PageInfo!) {
  eventVenueSeatTypes(venueId: $venueId, page: $page) {
    nodes { id, name, description }
    totalCount
    pageInfo { hasNextPage, endCursor }
  }
}

mutation ($venueId: ID!, $input: EventVenueSeatTypeCreateInput!) {
  eventVenueSeatTypeCreate(venueId: $venueId, input: $input) { id }
}

mutation ($id: ID!, $input: EventVenueSeatTypeUpdateInput!) {
  eventVenueSeatTypeUpdate(id: $id, input: $input) { id }
}

mutation ($id: ID!) {
  eventVenueSeatTypeDelete(id: $id) { id }
}`}
                    </GqlBlock>
                  </div>
                  <Divider />
                  <div>
                    <Text fw={600} mb="xs">
                      Seat Operations
                    </Text>
                    <GqlBlock>
                      {`query ($eventId: ID!, $eventVenueId: ID, $page: PageInfo!,
       $reservationIds: [ID!]) {
  eventSeats(
    eventId: $eventId, eventVenueId: $eventVenueId,
    page: $page, reservationIds: $reservationIds
  ) {
    nodes { id, seatTypeId, x, y, width, height }
    totalCount
    pageInfo { hasNextPage, endCursor }
  }
}

query ($venueId: ID!, $seatTypeId: ID, $page: PageInfo!) {
  eventVenueSeats(
    venueId: $venueId, seatTypeId: $seatTypeId, page: $page
  ) {
    nodes { id, seatTypeId, x, y, width, height }
    totalCount
    pageInfo { hasNextPage, endCursor }
  }
}

# Bulk seat management
mutation ($venueId: ID!, $input: EventVenueSeatsUpdateInput!) {
  eventVenueSeatsUpdate(venueId: $venueId, input: $input) {
    created, updated, deleted
  }
}`}
                    </GqlBlock>
                  </div>
                </Stack>
              </Accordion.Panel>
            </Accordion.Item>

            <Accordion.Item value="input-types">
              <Accordion.Control>
                <Group gap="xs">
                  <Badge color="gray" className={classes.mutationBadge}>
                    INPUTS
                  </Badge>
                  <Text>Input Types Reference</Text>
                </Group>
              </Accordion.Control>
              <Accordion.Panel>
                <Stack gap="md">
                  <div>
                    <Text fw={600} mb="xs">
                      Event Inputs
                    </Text>
                    <GqlBlock>
                      {`input EventCreateInput {
  title: String!
  description: String!
  eventVenueId: ID
  startAt: DateTime!
  endAt: DateTime!
  startRegistrationsAt: DateTime
  endRegistrationsAt: DateTime
  visibleAt: DateTime
  configuration: EventConfigurationInput
}

input EventUpdateInput {
  title: String
  description: String
  eventVenueId: ID
  startAt: DateTime
  endAt: DateTime
  startRegistrationsAt: DateTime
  endRegistrationsAt: DateTime
  visibleAt: DateTime
  configuration: EventConfigurationInput
}`}
                    </GqlBlock>
                  </div>
                  <Divider />
                  <div>
                    <Text fw={600} mb="xs">
                      Event Configuration
                    </Text>
                    <GqlBlock>
                      {`input EventConfigurationInput {
  customFields: [PropertyInput!]
  groupConfiguration: EventGroupConfigurationInput    # minSize, maxSize
  maxTicketsPerReservation: Float                     # must be >= groupConfiguration.maxSize
  reservationStatusAfterPayment: EventReservationStatus
  reservationStatusAfterRegistration: EventReservationStatus
  seatAssignmentMode: SeatAssignmentMode              # OPTIONAL, REQUIRED, DISABLED
  sessionDuration: Float                               # seconds (default: 900)
  allowForNewSessionAfterTimeout: Boolean
  allowForMoreTicketsThanGroupMembers: Boolean
}`}
                    </GqlBlock>
                  </div>
                  <Divider />
                  <div>
                    <Text fw={600} mb="xs">
                      Reservation Query Filters
                    </Text>
                    <GqlBlock>
                      {`input EventReservationsQueryInput {
  filterByGroupStatus: UserGroupStatus
  filterByPlayerProperties: [PropertyValueInput!]
  filterByPlayerIdentityProviderProperties: [PropertyValueInput!]
  filterByPlayerUsernameQuery: String
  filterByRegistrationStatus: EventReservationStatus
  filterByReservationProperties: [PropertyValueInput!]
  filterByPlayers: [ID!]
  retrieveOnlyGroupMembersWithStatus: UserGroupStatus
}`}
                    </GqlBlock>
                  </div>
                  <Divider />
                  <div>
                    <Text fw={600} mb="xs">
                      Payment & Ticket Inputs
                    </Text>
                    <GqlBlock>
                      {`input EventReservationValidateAndPayInput {
  canceledUrl: String
  returnUrl: String
  successUrl: String
  uiMode: String              # Stripe checkout mode
}

input EventReservationUpdateTicketsInput {
  addTickets: [AddTicketInput!]
  releaseTickets: [ID!]
  updateTickets: [UpdateTicketInput!]
}

input EventTicketConfigurationCreateInput {
  name: String!
  description: String
  eventId: ID!
  maxCount: Float!
  price: Float!
  currency: String!
  buyable: Boolean!
  seatTypeId: ID
  configuration: EventTicketConfigurationConfigurationInput
}

input EventVenueCreateInput {
  name: String!
  description: String
  address: String
  city: String
  state: String
  postalCode: String
  country: String
}

input EventVenueSeatsUpdateInput {
  createSeats: [CreateSeatInput!]
  updateSeats: [UpdateSeatInput!]
  deleteSeats: [ID!]
}`}
                    </GqlBlock>
                  </div>
                </Stack>
              </Accordion.Panel>
            </Accordion.Item>
          </Accordion>
        </div>

        {/* ===================== STATUS REFERENCE ===================== */}
        <div className={classes.section}>
          <SectionHeader
            icon={<IconListDetails size={18} />}
            title="Status Reference"
            id="statuses"
          />

          <SimpleGrid cols={{ base: 1, md: 2 }}>
            <Card className={classes.card} p="md" radius="md">
              <Text fw={600} mb="sm">
                Reservation Status
              </Text>
              <Table className={classes.statusTable}>
                <tbody>
                  <tr>
                    <td>
                      <Badge color="gray" variant="light">
                        NONE
                      </Badge>
                    </td>
                    <td>Just registered</td>
                  </tr>
                  <tr>
                    <td>
                      <Badge color="yellow" variant="light">
                        REQUIRES_ADMIN_APPROVAL
                      </Badge>
                    </td>
                    <td>Awaiting admin approval</td>
                  </tr>
                  <tr>
                    <td>
                      <Badge color="blue" variant="light">
                        PENDING
                      </Badge>
                    </td>
                    <td>Active payment session</td>
                  </tr>
                  <tr>
                    <td>
                      <Badge color="orange" variant="light">
                        PAYMENT_PENDING
                      </Badge>
                    </td>
                    <td>Payment being processed</td>
                  </tr>
                  <tr>
                    <td>
                      <Badge color="red" variant="light">
                        PAYMENT_FAILED
                      </Badge>
                    </td>
                    <td>Payment declined</td>
                  </tr>
                  <tr>
                    <td>
                      <Badge color="red" variant="light">
                        PAYMENT_REFUNDED
                      </Badge>
                    </td>
                    <td>Payment refunded</td>
                  </tr>
                  <tr>
                    <td>
                      <Badge color="dark" variant="light">
                        SESSION_EXPIRED
                      </Badge>
                    </td>
                    <td>Session timed out</td>
                  </tr>
                  <tr>
                    <td>
                      <Badge color="cyan" variant="light">
                        REGISTERED
                      </Badge>
                    </td>
                    <td>Paid, awaiting approval</td>
                  </tr>
                  <tr>
                    <td>
                      <Badge color="yellow" variant="light">
                        PRESENCE_CONFIRMATION_PENDING
                      </Badge>
                    </td>
                    <td>Awaiting presence check</td>
                  </tr>
                  <tr>
                    <td>
                      <Badge color="green" variant="light">
                        CONFIRMED
                      </Badge>
                    </td>
                    <td>Fully confirmed</td>
                  </tr>
                  <tr>
                    <td>
                      <Badge color="gray" variant="light">
                        NOT_ATTENDING
                      </Badge>
                    </td>
                    <td>Not attending</td>
                  </tr>
                  <tr>
                    <td>
                      <Badge color="red" variant="light">
                        DENIED
                      </Badge>
                    </td>
                    <td>Denied by admin</td>
                  </tr>
                </tbody>
              </Table>
            </Card>

            <Card className={classes.card} p="md" radius="md">
              <Text fw={600} mb="sm">
                Group Status
              </Text>
              <Table className={classes.statusTable}>
                <tbody>
                  <tr>
                    <td>
                      <Badge color="green" variant="light">
                        VALID
                      </Badge>
                    </td>
                    <td>Meets size requirements</td>
                  </tr>
                  <tr>
                    <td>
                      <Badge color="red" variant="light">
                        INVALID
                      </Badge>
                    </td>
                    <td>Does not meet requirements</td>
                  </tr>
                </tbody>
              </Table>

              <Text fw={600} mb="sm" mt="lg">
                Seat Assignment Mode
              </Text>
              <Table className={classes.statusTable}>
                <tbody>
                  <tr>
                    <td>
                      <Badge color="green" variant="light">
                        OPTIONAL
                      </Badge>
                    </td>
                    <td>Users can choose a seat</td>
                  </tr>
                  <tr>
                    <td>
                      <Badge color="blue" variant="light">
                        REQUIRED
                      </Badge>
                    </td>
                    <td>Users must select a seat</td>
                  </tr>
                  <tr>
                    <td>
                      <Badge color="gray" variant="light">
                        DISABLED
                      </Badge>
                    </td>
                    <td>No seat selection</td>
                  </tr>
                </tbody>
              </Table>
            </Card>
          </SimpleGrid>
        </div>

        {/* ===================== CUSTOM FIELDS & METADATA ===================== */}
        <div className={classes.section}>
          <SectionHeader
            icon={<IconDatabase size={18} />}
            title="Custom Fields & Metadata (Data Service)"
            id="custom-fields-metadata"
          />
          <Text mb="md">
            In addition to the event-specific and ticket-level custom fields
            configured in event settings, events also support the platform-wide{" "}
            <Text fw={700} span>
              Custom Fields
            </Text>{" "}
            and{" "}
            <Text fw={700} span>
              Object Metadata
            </Text>{" "}
            systems from the Data service.
          </Text>
          <List spacing="xs" size="sm" mb="md">
            <List.Item>
              <Text fw={600} span>
                Custom Fields (Data service)
              </Text>{" "}
              — Structured, typed fields defined in Data &gt; Custom Fields.
              Supports validation, visibility, and editability controls.
              Available for <Code>Event</Code> and <Code>EventVenue</Code>{" "}
              object types.
            </List.Item>
            <List.Item>
              <Text fw={600} span>
                Object Metadata
              </Text>{" "}
              — Flexible key-value store for attaching arbitrary data. Each
              entry has a type (String, Boolean, URL, Image URL) and visibility
              (Public or Private).
            </List.Item>
          </List>
          <Text mb="sm">
            Manage these in the event&apos;s settings screens (Custom Field
            Values and Metadata tabs) or via the GraphQL API:
          </Text>
          <GqlBlock>
            {`# Query custom field values for an event
query ($objectType: String!, $objectId: ID!) {
  customFieldValues(objectType: $objectType, objectId: $objectId) {
    definition { key, name, type, required }
    value
  }
}
# objectType: "Event" or "EventVenue"

# Query metadata for an event
query ($objectType: String!, $objectId: ID!) {
  objectMetadata(objectType: $objectType, objectId: $objectId) {
    key, value, type, visibility
  }
}`}
          </GqlBlock>
          <Alert icon={<IconInfoCircle />} color="cyan" variant="light" mt="md">
            See the{" "}
            <Anchor href="/data/documentations">Data Documentation</Anchor> for
            the full Custom Fields & Metadata API reference, including how to
            create definitions, set values, and manage metadata entries.
          </Alert>
        </div>
      </Stack>
    </div>
  );
};
